// pkg45 — docx-preview 'experimental' enabled: tab-stop calculation, so tab-centered letterheads render centered instead of shifted right — delivered 2026-07-05
// pkg44 — preview scales to fit the panel (page rendered at true paper width overflowed right); caveat that the signature attaches to the exact document file; graceful awaiting-attorney state when the firm's fields are still blank — delivered 2026-07-04
// pkg41 — AcknowledgmentView: client reviews the FROZEN document snapshot in-portal (docx-preview) and signs with a typed name + attestation checkbox (D-41b/c/e/f); download of the acknowledged copy appears only after signing — delivered 2026-07-04
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { renderAsync } from 'docx-preview';
import { api } from '../utils/api';

// Flow (matches the pkg41 backend exactly):
//   1. POST .../acknowledgment/open   — creates or returns the 'pending'
//      snapshot (rendered ONCE server-side; what is shown IS what gets signed).
//   2. GET  .../document              — the snapshot bytes; docx-preview
//      renders them into the viewer below. View-only at this stage (D-41e).
//   3. POST .../sign { typed_name }   — binds the signature to the STORED
//      snapshot. A 409 ACK_STALE means the answers changed since the page
//      loaded (the pending row was invalidated) — we re-open automatically
//      so the client reviews the current version.
//   4. Signed → confirmation + "Download your acknowledged copy".

const font = { fontFamily: 'Arial, sans-serif' };

export default function AcknowledgmentView({ moduleId, moduleLabel, onBack }) {
  const [ack,       setAck]       = useState(null);   // { acknowledgment_id, filename, attestation_text, render_path }
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [staleNote, setStaleNote] = useState('');
  const [awaiting,  setAwaiting]  = useState(false); // pkg44 — attorney fields still blank

  const [agreed,    setAgreed]    = useState(false);
  const [typedName, setTypedName] = useState('');
  const [signing,   setSigning]   = useState(false);
  const [signed,    setSigned]    = useState(null);   // { acknowledgment_id, acknowledged_at }
  const [downloading, setDownloading] = useState(false);

  const viewerRef = useRef(null);

  // pkg44 — docx-preview renders the page at true paper width (8.5in ≈
  // 816px), which overflows narrower panels and makes centered headers look
  // shifted right. Scale the rendered page down to fit; layout is preserved.
  function fitToPanel() {
    const container = viewerRef.current;
    if (!container) return;
    const page = container.querySelector('.docx-wrapper');
    if (!page) return;
    page.style.transform = '';
    page.style.transformOrigin = 'top left';
    const avail = container.clientWidth;
    const natural = page.scrollWidth;
    if (natural > avail && natural > 0) {
      const scale = avail / natural;
      page.style.transform = `scale(${scale})`;
      // transform doesn't reflow height — clamp the container to scaled height
      container.style.height = `${Math.min(page.scrollHeight * scale, 560)}px`;
    } else {
      container.style.height = '';
    }
  }

  const openAndRender = useCallback(async () => {
    setLoading(true); setError(''); setAwaiting(false);
    try {
      const opened = await api.openMyAcknowledgment(moduleId);
      setAck(opened);
      const blob = await api.getMyAcknowledgmentDocBlob(moduleId, opened.acknowledgment_id);
      const buf  = await blob.arrayBuffer();
      if (viewerRef.current) {
        viewerRef.current.innerHTML = '';
        await renderAsync(buf, viewerRef.current, null, {
          inWrapper: true,
          ignoreLastRenderedPageBreak: false,
          experimental: true, // pkg45 — tab-stop calculation (tab-centered letterheads)
        });
        fitToPanel(); // pkg44
      }
    } catch (err) {
      if (err.code === 'AWAITING_ATTORNEY' || err.status === 409) { // pkg44
        setAwaiting(true);
      } else {
        setError(err.message || 'Could not load the document. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [moduleId]);

  useEffect(() => { // pkg44 — re-fit on window resize
    const onResize = () => fitToPanel();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => { openAndRender(); }, [openAndRender]);

  async function handleSign() {
    if (!agreed || !typedName.trim() || !ack) return;
    setSigning(true); setError(''); setStaleNote('');
    try {
      const r = await api.signMyAcknowledgment(moduleId, ack.acknowledgment_id, typedName.trim());
      setSigned(r);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      if (err.code === 'ACK_STALE' || err.status === 409) {
        // Answers changed since this snapshot was created — reload the
        // current version so what's shown is what gets signed.
        setStaleNote('This document was updated since you opened it. Please review the current version below and sign again.');
        setAgreed(false);
        await openAndRender();
      } else {
        setError(err.message || 'Signing failed. Please try again.');
      }
    } finally {
      setSigning(false);
    }
  }

  async function handleDownload() {
    if (!signed) return;
    setDownloading(true); setError('');
    try {
      const blob = await api.downloadMyAcknowledgedCopy(moduleId, signed.acknowledgment_id);
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = ack?.filename || 'acknowledged-document.docx';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  }

  // ── Signed confirmation ───────────────────────────────────────────────────
  if (signed) {
    return (
      <div style={{
        background: '#f0faf5', border: '1px solid #4caf7d',
        borderRadius: 6, padding: '20px 24px', maxWidth: 560, ...font,
      }}>
        <div style={{ fontSize: 22, marginBottom: 8 }}>✓</div>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: '#1a3a2a' }}>
          Document acknowledged
        </div>
        <div style={{ fontSize: 14, color: '#444', marginBottom: 4 }}>
          You signed "{moduleLabel}" on {new Date(signed.acknowledged_at).toLocaleString()}.
        </div>
        <div style={{ fontSize: 13, color: '#666' }}>
          Your attorney has been notified. An exact copy of the document as you acknowledged it has been preserved.
        </div>
        {error && (
          <div style={{ fontSize: 13, color: '#b91c1c', marginTop: 10 }}>{error}</div>
        )}
        <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            onClick={handleDownload}
            disabled={downloading}
            style={{
              fontSize: 13, ...font, padding: '8px 18px', borderRadius: 4, border: 'none',
              background: '#1a3a2a', color: '#fff', cursor: 'pointer', fontWeight: 600,
              opacity: downloading ? 0.7 : 1,
            }}
          >
            {downloading ? 'Downloading…' : '↓ Download your acknowledged copy'}
          </button>
          {onBack && (
            <button
              onClick={onBack}
              style={{
                fontSize: 13, ...font, padding: '8px 18px', borderRadius: 4, border: 'none',
                background: '#eef2f7', color: '#1a3a2a', cursor: 'pointer', fontWeight: 600,
              }}
            >
              Back to the form
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Awaiting the attorney (pkg44) ─────────────────────────────────────────
  if (awaiting) {
    return (
      <div style={{
        background: '#f8fafb', border: '1px solid #dde3ec',
        borderRadius: 6, padding: '20px 24px', maxWidth: 560, ...font,
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: '#1a3a2a' }}>
          Your attorney is finishing this document
        </div>
        <div style={{ fontSize: 14, color: '#444' }}>
          Your answers are in. Your attorney is completing their portion of the document —
          you'll be able to review and sign it here once it's ready.
        </div>
        {onBack && (
          <button
            onClick={onBack}
            style={{
              marginTop: 14, fontSize: 13, ...font, padding: '7px 16px', borderRadius: 4,
              border: 'none', background: '#eef2f7', color: '#1a3a2a', cursor: 'pointer', fontWeight: 600,
            }}
          >
            Back to the form
          </button>
        )}
      </div>
    );
  }

  // ── Review & sign ─────────────────────────────────────────────────────────
  const canSign = agreed && typedName.trim().length > 0 && !loading && !signing && !!ack;

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ fontSize: 14, color: '#555', marginBottom: 12, ...font }}>
        Review the document below. When you're ready, confirm and sign at the bottom.
      </div>

      {staleNote && (
        <div style={{
          background: '#fff8e1', border: '1px solid #f5c842', borderRadius: 4,
          padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#7c5200', ...font,
        }}>
          {staleNote}
        </div>
      )}

      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4,
          padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#b91c1c', ...font,
        }}>
          {error}
        </div>
      )}

      {/* Document viewer — docx-preview renders the snapshot bytes here. */}
      <div style={{ border: '1px solid #dde3ec', borderRadius: 6, background: '#f4f6f9' }}>
        <div style={{
          borderBottom: '1px solid #dde3ec', padding: '8px 14px',
          fontSize: 12, color: '#666', display: 'flex', justifyContent: 'space-between', ...font,
        }}>
          <span>{ack?.filename || 'Document'}</span>
          <span>View only until signed</span>
        </div>
        <div
          ref={viewerRef}
          style={{ maxHeight: 560, overflowY: 'auto', overflowX: 'hidden', padding: '12px 0' }}
        />
        {loading && (
          <div style={{ padding: 30, textAlign: 'center', fontSize: 13, color: '#888', ...font }}>
            Preparing your document…
          </div>
        )}
      </div>

      {/* Attestation + typed-name signature (D-41f option 2) */}
      <div style={{ marginTop: 18, borderTop: '1px solid #eee', paddingTop: 16 }}>
        <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, lineHeight: 1.5, cursor: 'pointer', ...font }}>
          <input
            type="checkbox"
            checked={agreed}
            onChange={e => setAgreed(e.target.checked)}
            style={{ marginTop: 2, width: 16, height: 16, cursor: 'pointer' }}
          />
          <span>{ack?.attestation_text || 'I confirm that I have read and reviewed this document in its entirety, and I acknowledge and agree to its terms. I intend my typed name to serve as my electronic signature.'}</span>
        </label>

        <div style={{ display: 'flex', gap: 12, marginTop: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4, ...font }}>
              Type your full legal name to sign
            </label>
            <input
              type="text"
              value={typedName}
              onChange={e => setTypedName(e.target.value)}
              placeholder="Your full legal name"
              maxLength={200}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '8px 11px',
                fontSize: 14, border: '1px solid #ccc', borderRadius: 4, ...font,
              }}
            />
          </div>
          <button
            onClick={handleSign}
            disabled={!canSign}
            style={{
              fontSize: 14, ...font, padding: '10px 24px', borderRadius: 4, border: 'none',
              background: '#1a3a2a', color: '#fff', fontWeight: 600,
              cursor: canSign ? 'pointer' : 'default',
              opacity: canSign ? 1 : 0.5,
            }}
          >
            {signing ? 'Signing…' : 'Acknowledge & sign'}
          </button>
        </div>

        <div style={{ fontSize: 11, color: '#888', marginTop: 10, ...font }}>
          Signing records your name, the date and time, and your IP address, together with an exact copy of this document. The preview above is a close approximation for reading on screen — your signature attaches to the exact document file, which you can download after signing.
        </div>
      </div>
    </div>
  );
}
